import { useState, useMemo, useCallback } from 'react';
import { useFlightSearch } from '../hooks/useFlightSearch';
import FlightCard from './FlightCard';
import FlightFilters from './FlightFilters';
import {
  Plane,
  Users,
  Calendar,
  Loader2,
  ArrowRightLeft,
  Settings,
  Search,
  X
} from 'lucide-react';
import type {
  FlightSearchParams,
  FlightFilters as FlightFiltersType,
  FlightSortOption,
  CabinClass,
  FlightOffer
} from '../types/flight';

// Import actual FlightOffer type from hook for internal use
import type { FlightOffer as FlightSearchResult } from '../hooks/useFlightSearch';

interface FlightSearchProps {
  onSelectFlight?: (flight: FlightOffer) => void;
  onAddToItinerary?: (flight: FlightOffer) => void;
  className?: string;
}

type TripType = 'one-way' | 'round-trip';

interface SearchFormData {
  origin: {
    code: string;
    city: string;
  };
  destination: {
    code: string;
    city: string;
  };
  departDate: string;
  returnDate?: string;
  tripType: TripType;
  passengers: {
    adults: number;
    children: number;
    infants: number;
  };
  cabinClass: 'ECONOMY' | 'PREMIUM_ECONOMY' | 'BUSINESS' | 'FIRST';
  directOnly: boolean;
}

const defaultFilters: FlightFiltersType = {
  priceRange: { min: 0, max: 5000 },
  stops: { nonStop: true, oneStop: true, twoOrMoreStops: true },
  departureTime: { morning: true, afternoon: true, evening: true, night: true },
  airlines: [],
  cabinClass: ['ECONOMY', 'PREMIUM_ECONOMY', 'BUSINESS', 'FIRST'],
  airports: { origin: [], destination: [] },
  duration: { maxHours: 24 },
  sortBy: 'price-asc',
};

export default function FlightSearch({
  onSelectFlight,
  onAddToItinerary,
  className = ''
}: FlightSearchProps) {
  const {
    results,
    loading,
    error,
    search,
    clearResults,
  } = useFlightSearch();

  const [isSearchExpanded, setIsSearchExpanded] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [searchForm, setSearchForm] = useState<SearchFormData>({
    origin: { code: '', city: '' },
    destination: { code: '', city: '' },
    departDate: '',
    returnDate: '',
    tripType: 'round-trip',
    passengers: { adults: 1, children: 0, infants: 0 },
    cabinClass: 'ECONOMY',
    directOnly: false,
  });

  const [filters, setFilters] = useState<FlightFiltersType>(defaultFilters);
  const [sortBy, setSortBy] = useState<FlightSortOption>('price-asc');

  // Convert form data to search params
  const searchParams: FlightSearchParams | null = useMemo(() => {
    if (!searchForm.origin.code || !searchForm.destination.code || !searchForm.departDate) {
      return null;
    }

    return {
      origin: searchForm.origin.code,
      destination: searchForm.destination.code,
      departDate: searchForm.departDate,
      returnDate: searchForm.tripType === 'round-trip' ? searchForm.returnDate : undefined,
      passengers: searchForm.passengers,
      cabinClass: searchForm.cabinClass,
      trips: [searchForm.tripType === 'round-trip' ? 'round-trip' : 'one-way'],
      maxResults: 20,
    };
  }, [searchForm]);

  // Helper function to parse ISO duration to minutes
  const parseDuration = (isoDuration: string): number => {
    const match = isoDuration.match(/PT(?:(\d+)H)?(?:(\d+)M)?/);
    if (!match) return 0;
    const hours = parseInt(match[1] || '0');
    const minutes = parseInt(match[2] || '0');
    return hours * 60 + minutes;
  };

  // Transform API response to comprehensive FlightOffer type
  const transformFlightOffer = (apiOffer: FlightSearchResult) => {
    // Parse duration from ISO format
    const parseDurationStr = (isoDuration: string): { totalMinutes: number; hours: number; minutes: number } => {
      const match = isoDuration.match(/PT(?:(\d+)H)?(?:(\d+)M)?/);
      if (!match) return { totalMinutes: 0, hours: 0, minutes: 0 };
      const hours = parseInt(match[1] || '0');
      const minutes = parseInt(match[2] || '0');
      return { totalMinutes: hours * 60 + minutes, hours, minutes };
    };

    const durationInfo = parseDurationStr(apiOffer.duration);

    return {
      id: apiOffer.id,
      airline: apiOffer.airline,
      airlineLogo: undefined,
      flightNumber: apiOffer.flightNumber,
      aircraft: undefined,
      origin: {
        code: apiOffer.origin,
        name: apiOffer.origin,
        city: '',
        country: '',
      },
      destination: {
        code: apiOffer.destination,
        name: apiOffer.destination,
        city: '',
        country: '',
      },
      departureTime: apiOffer.departureTime,
      arrivalTime: apiOffer.arrivalTime,
      duration: {
        totalMinutes: durationInfo.totalMinutes,
        hours: durationInfo.hours,
        minutes: durationInfo.minutes,
        isoDuration: apiOffer.duration,
      },
      stops: apiOffer.stops,
      layovers: [],
      price: {
        total: apiOffer.price,
        base: apiOffer.price,
        taxes: 0,
        fees: 0,
        currency: apiOffer.currency,
      },
      seatsAvailable: 0,
      cabinClass: apiOffer.cabinClass as CabinClass,
      amenities: apiOffer.amenities || [],
      baggage: undefined,
      cancellationPolicy: undefined,
      searchParams: searchParams!,
      provider: 'Amadeus',
    };
  };

  // Apply filters and sorting to results
  const filteredResults = useMemo(() => {
    let filtered = [...results];

    // Apply stops filter - use the simplified stops property
    filtered = filtered.filter(offer => {
      const stops = offer.stops;
      if (stops === 0 && !filters.stops.nonStop) return false;
      if (stops === 1 && !filters.stops.oneStop) return false;
      if (stops >= 2 && !filters.stops.twoOrMoreStops) return false;
      return true;
    });

    // Apply price filter
    filtered = filtered.filter(offer => {
      const priceNum = offer.price;
      return priceNum >= filters.priceRange.min &&
             priceNum <= filters.priceRange.max;
    });

    // Apply airline filter - use the simplified airline property
    if (filters.airlines.length > 0) {
      filtered = filtered.filter(offer => {
        return filters.airlines.includes(offer.airline);
      });
    }

    // Apply direct only filter
    if (searchForm.directOnly) {
      filtered = filtered.filter(offer => {
        return offer.stops === 0;
      });
    }

    // Apply sorting
    switch (sortBy) {
      case 'price-asc':
        filtered.sort((a, b) => a.price - b.price);
        break;
      case 'price-desc':
        filtered.sort((a, b) => b.price - a.price);
        break;
      case 'duration-asc':
        filtered.sort((a, b) => {
          const durationA = parseDuration(a.duration);
          const durationB = parseDuration(b.duration);
          return durationA - durationB;
        });
        break;
      case 'departure-asc':
        filtered.sort((a, b) =>
          new Date(a.departureTime).getTime() - new Date(b.departureTime).getTime()
        );
        break;
      case 'stops-asc':
        filtered.sort((a, b) => a.stops - b.stops);
        break;
    }

    return filtered;
  }, [results, filters, sortBy, searchForm.directOnly]);

  // Get unique airlines for filter
  const availableAirlines = useMemo(() => {
    const airlines = new Set<string>();
    results.forEach(offer => {
      if (offer.airline) {
        airlines.add(offer.airline);
      }
    });
    return Array.from(airlines).sort();
  }, [results]);

  const handleSearch = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    if (!searchParams) return;

    clearResults();
    // Convert to the format the hook expects
    await search({
      originLocationCode: searchParams.origin,
      destinationLocationCode: searchParams.destination,
      departureDate: searchParams.departDate,
      returnDate: searchParams.returnDate,
      adults: searchParams.passengers.adults,
      travelClass: searchParams.cabinClass,
      nonStop: searchParams.nonStopOnly || false,
    });
  }, [searchParams, search, clearResults]);

  const handleSwapLocations = () => {
    setSearchForm(prev => ({
      ...prev,
      origin: prev.destination,
      destination: prev.origin,
    }));
  };

  const handleClearSearch = () => {
    clearResults();
    setSearchForm({
      origin: { code: '', city: '' },
      destination: { code: '', city: '' },
      departDate: '',
      returnDate: '',
      tripType: 'round-trip',
      passengers: { adults: 1, children: 0, infants: 0 },
      cabinClass: 'ECONOMY',
      directOnly: false,
    });
  };

  const updatePassengers = (type: keyof SearchFormData['passengers'], delta: number) => {
    setSearchForm(prev => ({
      ...prev,
      passengers: {
        ...prev.passengers,
        [type]: Math.max(0, prev.passengers[type] + delta),
      },
    }));
  };

  const totalPassengers = useMemo(() => 
    searchForm.passengers.adults + 
    searchForm.passengers.children + 
    searchForm.passengers.infants,
    [searchForm.passengers]
  );

  const today = new Date().toISOString().split('T')[0];
  const minReturnDate = searchForm.departDate || today;

  return (
    <div className={`bg-white rounded-xl shadow-sm border border-gray-100 ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-100">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-primary-100 rounded-lg">
            <Plane className="h-5 w-5 text-primary-600" />
          </div>
          <div>
            <h2 className="font-semibold text-gray-900">Flight Search</h2>
            <p className="text-sm text-gray-500">
              {filteredResults.length} flights found
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`p-2 rounded-lg transition-colors ${
              showFilters ? 'bg-primary-100 text-primary-600' : 'text-gray-400 hover:text-gray-600'
            }`}
          >
            <Settings className="h-5 w-5" />
          </button>
          <button
            onClick={() => setIsSearchExpanded(!isSearchExpanded)}
            className="text-gray-400 hover:text-gray-600"
          >
            {isSearchExpanded ? (
              <X className="h-5 w-5" />
            ) : (
              <Search className="h-5 w-5" />
            )}
          </button>
        </div>
      </div>

      {/* Search Form */}
      {isSearchExpanded && (
        <form onSubmit={handleSearch} className="p-4 space-y-4">
          {/* Trip Type */}
          <div className="flex gap-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="tripType"
                value="round-trip"
                checked={searchForm.tripType === 'round-trip'}
                onChange={() => setSearchForm(prev => ({ ...prev, tripType: 'round-trip' }))}
                className="text-primary-600 focus:ring-primary-500"
              />
              <span className="text-sm font-medium text-gray-700">Round Trip</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="tripType"
                value="one-way"
                checked={searchForm.tripType === 'one-way'}
                onChange={() => setSearchForm(prev => ({ ...prev, tripType: 'one-way' }))}
                className="text-primary-600 focus:ring-primary-500"
              />
              <span className="text-sm font-medium text-gray-700">One Way</span>
            </label>
          </div>

          {/* Main Search Fields */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Origin */}
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                From
              </label>
              <div className="relative">
                <input
                  type="text"
                  placeholder="City or airport (e.g., LAX)"
                  className="input-field pl-9"
                  value={searchForm.origin.city}
                  onChange={(e) => setSearchForm(prev => ({
                    ...prev,
                    origin: { ...prev.origin, city: e.target.value, code: e.target.value.slice(0, 3).toUpperCase() }
                  }))}
                  maxLength={3}
                />
                <Plane className="h-4 w-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
              </div>
            </div>

            {/* Swap Button */}
            <div className="hidden lg:flex items-center justify-center">
              <button
                type="button"
                onClick={handleSwapLocations}
                className="p-2 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors"
              >
                <ArrowRightLeft className="h-4 w-4 text-gray-600" />
              </button>
            </div>

            {/* Destination */}
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                To
              </label>
              <div className="relative">
                <input
                  type="text"
                  placeholder="City or airport (e.g., JFK)"
                  className="input-field pl-9"
                  value={searchForm.destination.city}
                  onChange={(e) => setSearchForm(prev => ({
                    ...prev,
                    destination: { ...prev.destination, city: e.target.value, code: e.target.value.slice(0, 3).toUpperCase() }
                  }))}
                  maxLength={3}
                />
                <Plane className="h-4 w-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2 rotate-90" />
              </div>
            </div>

            {/* Dates */}
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  <Calendar className="h-3 w-3 inline mr-1" />
                  Depart
                </label>
                <input
                  type="date"
                  className="input-field"
                  value={searchForm.departDate}
                  onChange={(e) => setSearchForm(prev => ({ ...prev, departDate: e.target.value }))}
                  min={today}
                  required
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  Return
                </label>
                <input
                  type="date"
                  className="input-field"
                  value={searchForm.returnDate}
                  onChange={(e) => setSearchForm(prev => ({ ...prev, returnDate: e.target.value }))}
                  min={minReturnDate}
                  disabled={searchForm.tripType === 'one-way'}
                />
              </div>
            </div>
          </div>

          {/* Passengers and Class */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Passengers */}
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                <Users className="h-3 w-3 inline mr-1" />
                Passengers
              </label>
              <div className="flex items-center gap-2">
                <div className="flex-1">
                  <select
                    className="input-field"
                    value={searchForm.passengers.adults}
                    onChange={(e) => updatePassengers('adults', parseInt(e.target.value) - searchForm.passengers.adults)}
                  >
                    {[1, 2, 3, 4, 5, 6, 7, 8, 9].map(n => (
                      <option key={n} value={n}>{n} Adult{totalPassengers > 1 ? 's' : ''}</option>
                    ))}
                  </select>
                </div>
                {searchForm.passengers.children > 0 && (
                  <span className="text-xs text-gray-500">
                    +{searchForm.passengers.children} child
                  </span>
                )}
              </div>
            </div>

            {/* Cabin Class */}
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Cabin Class
              </label>
              <select
                className="input-field"
                value={searchForm.cabinClass}
                onChange={(e) => setSearchForm(prev => ({ 
                  ...prev, 
                  cabinClass: e.target.value as SearchFormData['cabinClass'] 
                }))}
              >
                <option value="ECONOMY">Economy</option>
                <option value="PREMIUM_ECONOMY">Premium Economy</option>
                <option value="BUSINESS">Business</option>
                <option value="FIRST">First Class</option>
              </select>
            </div>

            {/* Direct Flights Only */}
            <div className="flex items-end">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={searchForm.directOnly}
                  onChange={(e) => setSearchForm(prev => ({ 
                    ...prev, 
                    directOnly: e.target.checked 
                  }))}
                  className="rounded text-primary-600 focus:ring-primary-500"
                />
                <span className="text-sm text-gray-700">Direct flights only</span>
              </label>
            </div>

            {/* Search Button */}
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                &nbsp;
              </label>
              <button
                type="submit"
                disabled={loading || !searchParams}
                className="btn-primary w-full flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Searching...
                  </>
                ) : (
                  <>
                    <Search className="h-4 w-4" />
                    Search Flights
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Clear Search */}
          {results.length > 0 && (
            <button
              type="button"
              onClick={handleClearSearch}
              className="text-sm text-gray-500 hover:text-gray-700 underline"
            >
              Clear search and start over
            </button>
          )}
        </form>
      )}

      {/* Filters Panel */}
      {showFilters && results.length > 0 && (
        <div className="border-t border-gray-100 p-4 bg-gray-50">
          <FlightFilters
            filters={filters}
            onFiltersChange={setFilters}
            sortBy={sortBy}
            onSortChange={setSortBy}
            availableAirlines={availableAirlines}
          />
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="p-4 bg-red-50 border-t border-red-100">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      {/* Results */}
      {filteredResults.length > 0 && (
        <div className="border-t border-gray-100">
          {/* Sort Bar */}
          <div className="flex items-center justify-between p-4 border-b border-gray-100">
            <span className="text-sm text-gray-600">
              {filteredResults.length} flights
            </span>
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-500">Sort by:</span>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as FlightSortOption)}
                className="text-sm border-0 bg-transparent focus:ring-0 text-gray-700"
              >
                <option value="price-asc">Price: Low to High</option>
                <option value="price-desc">Price: High to Low</option>
                <option value="duration-asc">Duration: Shortest</option>
                <option value="departure-asc">Departure: Earliest</option>
                <option value="stops-asc">Stops: Fewest</option>
              </select>
            </div>
          </div>

          {/* Flight Cards */}
          <div className="divide-y divide-gray-100">
            {filteredResults.map((flight, index) => (
              <FlightCard
                key={flight.id || index}
                flight={transformFlightOffer(flight)}
                onSelect={onSelectFlight}
                onAddToItinerary={onAddToItinerary}
              />
            ))}
          </div>

          {/* Load More */}
          {/* Pagination not supported by current API - keeping for future */}
          {false && (
            <div className="p-4 text-center">
              <button
                onClick={() => {
                  if (!searchParams) return;
                  search({
                    originLocationCode: searchParams.origin,
                    destinationLocationCode: searchParams.destination,
                    departureDate: searchParams.departDate,
                    returnDate: searchParams.returnDate,
                    adults: searchParams.passengers.adults,
                    travelClass: searchParams.cabinClass,
                    nonStop: searchParams.nonStopOnly || false,
                  });
                }}
                disabled={loading}
                className="btn-secondary"
              >
                {loading ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : null}
                Load More Flights
              </button>
            </div>
          )}
        </div>
      )}

      {/* No Results */}
      {results.length > 0 && filteredResults.length === 0 && !loading && (
        <div className="p-8 text-center">
          <Plane className="h-12 w-12 text-gray-300 mx-auto mb-3" />
          <h3 className="text-lg font-medium text-gray-900 mb-1">No flights found</h3>
          <p className="text-sm text-gray-500 mb-4">
            Try adjusting your filters or search criteria
          </p>
          <button
            onClick={() => setFilters(defaultFilters)}
            className="btn-secondary"
          >
            Clear Filters
          </button>
        </div>
      )}

      {/* Initial State */}
      {results.length === 0 && !loading && (
        <div className="p-8 text-center">
          <Plane className="h-12 w-12 text-gray-300 mx-auto mb-3" />
          <h3 className="text-lg font-medium text-gray-900 mb-1">Search for flights</h3>
          <p className="text-sm text-gray-500">
            Enter your travel details above to find the best flights
          </p>
        </div>
      )}
    </div>
  );
}
